import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Chip,
  Slide,
  Fade,
} from '@mui/material';
import {
  Close as CloseIcon,
  ReplyOutlined as ReplyIcon,
  SendOutlined as SendIcon,
} from '@mui/icons-material';

export interface HelpNotification {
  id: string;
  helpRequestId: string;
  sessionId: string;
  studentName: string;
  stepTitle?: string;
  stepOrderIndex?: number;
  message: string;
  timestamp: number;
}

interface HelpNotificationCardProps {
  notification: HelpNotification;
  index: number;
  onDismiss: (id: string) => void;
  onQuickReply: (helpRequestId: string, message: string) => Promise<boolean>;
}

export default function HelpNotificationCard({
  notification,
  index,
  onDismiss,
  onQuickReply,
}: HelpNotificationCardProps) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [exiting, setExiting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (replyOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [replyOpen]);

  const handleDismiss = () => {
    setExiting(true);
    setTimeout(() => onDismiss(notification.id), 250);
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || sending) return;
    setSending(true);
    const success = await onQuickReply(notification.helpRequestId, replyText.trim());
    setSending(false);
    if (success) {
      setExiting(true);
      setTimeout(() => onDismiss(notification.id), 250);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
    if (e.key === 'Escape') {
      setReplyOpen(false);
      setReplyText('');
    }
  };

  const stepLabel = notification.stepOrderIndex != null
    ? `Step ${notification.stepOrderIndex + 1}`
    : undefined;

  return (
    <Fade in={!exiting} timeout={250}>
      <Box>
        <Slide direction="left" in={!exiting} timeout={300}>
          <Box
            sx={{
              width: 380,
              bgcolor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              borderLeft: '4px solid #2a7b88',
              mb: 1.5,
              overflow: 'hidden',
              transition: 'transform 0.2s, opacity 0.2s',
              '&:hover': { boxShadow: '0 8px 28px rgba(0,0,0,0.2)' },
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                pt: 1.5,
                pb: 0.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 16 }}>🙋</Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {notification.studentName}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                {stepLabel && (
                  <Chip
                    label={stepLabel}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      bgcolor: 'rgba(42,123,136,0.1)',
                      color: '#2a7b88',
                    }}
                  />
                )}
                <IconButton size="small" onClick={handleDismiss} sx={{ ml: 0.5 }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>

            {/* Step title */}
            {notification.stepTitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ px: 2, display: 'block', lineHeight: 1.3 }}
              >
                {notification.stepTitle}
              </Typography>
            )}

            {/* Message body */}
            <Box sx={{ px: 2, py: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  fontStyle: 'italic',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.5,
                }}
              >
                &ldquo;{notification.message}&rdquo;
              </Typography>
            </Box>

            {/* Actions */}
            {!replyOpen ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                  px: 2,
                  pb: 1.5,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => setReplyOpen(true)}
                  sx={{
                    borderRadius: '8px',
                    px: 1.5,
                    fontSize: '0.75rem',
                    color: '#2a7b88',
                    '&:hover': { bgcolor: 'rgba(42,123,136,0.08)' },
                  }}
                >
                  <ReplyIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Quick Reply
                  </Typography>
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ px: 2, pb: 1.5, display: 'flex', gap: 0.5, alignItems: 'flex-end' }}>
                <TextField
                  inputRef={inputRef}
                  size="small"
                  fullWidth
                  multiline
                  maxRows={2}
                  placeholder="Type a reply..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={sending}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.8125rem' } }}
                />
                <IconButton
                  size="small"
                  onClick={handleSendReply}
                  disabled={!replyText.trim() || sending}
                  sx={{ color: '#2a7b88' }}
                >
                  <SendIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            )}
          </Box>
        </Slide>
      </Box>
    </Fade>
  );
}
